import { COACH_DISCLOSURE, type CoachDisclosure } from "./disclosure";
import type { MediaPermissionState } from "./mediaPermissions";

/**
 * Consent for one coaching session, as a pure reducer.
 *
 * Three things get conflated in camera features, and conflating them is the
 * compliance failure rather than a UX one:
 *
 * 1. The **device permission** — an OS grant. Technical, revocable in Settings,
 *    and not by itself a lawful basis for anything.
 * 2. **Processing** — may the live view be used while she injects.
 * 3. **Retention** — may anything be kept afterwards.
 *
 * This machine models the first two. It models the third by having no
 * representation for it at all: there is no state, event or field here that
 * could express "keep it", because the build has no path that could. Video of a
 * patient injecting a prescribed fertility medication is health data — special
 * category under GDPR Article 9, PHI under HIPAA — and retention would need an
 * explicit, demonstrable, withdrawable consent record. `/me/privacy` carries two
 * booleans and neither is this one, so there is nowhere to record it, and GDPR
 * Article 7(1) requires being *able to demonstrate* consent. A consent that
 * cannot be recorded cannot be relied on, so nothing is kept.
 *
 * That is why the promise is structural. A boolean the code could flip the wrong
 * way is not a control; an absent capability is. `no-retention.test.ts` asserts
 * the absence rather than trusting it.
 *
 * No clock, no I/O, no randomness — the same reason `injection-protocol/session`
 * is a reducer. What she was told, what she allowed and when it stopped must be
 * computed from events, never inferred.
 */

export type CoachConsentState =
  /**
   * She is reading what this is and what happens to the camera. Nothing has been
   * requested; the OS has not been touched.
   */
  | { status: "disclosure"; disclosure: CoachDisclosure }
  /** The OS prompt is up. Only reachable from `disclosure`, never before it. */
  | { status: "requesting-camera"; acknowledged: CoachDisclosure }
  /** Coaching is live. `camera` says whether the live view is on right now. */
  | {
      status: "coaching";
      acknowledged: CoachDisclosure;
      camera: boolean;
      /** Kept so the sheet can explain a refusal instead of silently going quiet. */
      cameraPermission: MediaPermissionState;
    }
  /** She stopped. Terminal for this session; a new one re-discloses. */
  | { status: "withdrawn"; acknowledged: CoachDisclosure | null };

export type CoachConsentEvent =
  /** "Continue with the camera" — the only path to a permission prompt. */
  | { type: "acknowledge-with-camera" }
  /** "Continue without it" — a first-class path, not a fallback. */
  | { type: "acknowledge-without-camera" }
  /** The OS answered. Carries the outcome, whatever it is. */
  | { type: "camera-permission-settled"; permission: MediaPermissionState }
  | { type: "turn-camera-off" }
  | { type: "turn-camera-on" }
  /** One tap, from anywhere, at any time. */
  | { type: "withdraw" };

/**
 * A fresh session, always at disclosure.
 *
 * Consent is not remembered between sessions, and that is deliberate rather than
 * a missing feature. There is nowhere to record it that would make it
 * demonstrable — local storage is neither durable evidence nor permitted here as
 * a data source — so each session asks again. The side effect is that consent is
 * always contemporaneous with the injection it covers, which is the stronger
 * position anyway.
 */
export function startCoachConsent(): CoachConsentState {
  return { status: "disclosure", disclosure: COACH_DISCLOSURE };
}

export function reduceCoachConsent(
  state: CoachConsentState,
  event: CoachConsentEvent,
): CoachConsentState {
  // Withdrawal outranks everything and is reachable from every live state. GDPR
  // Article 7(3) requires withdrawing to be as easy as consenting was; handling
  // it first means no state can accidentally swallow it.
  if (event.type === "withdraw") {
    if (state.status === "withdrawn") return state;
    return {
      status: "withdrawn",
      acknowledged:
        state.status === "disclosure" ? null : (state.acknowledged ?? null),
    };
  }

  // Terminal. A withdrawn session cannot be talked back into life; the caller
  // starts a new one, which shows the disclosure again.
  if (state.status === "withdrawn") return state;

  switch (state.status) {
    case "disclosure": {
      if (event.type === "acknowledge-with-camera") {
        return { status: "requesting-camera", acknowledged: state.disclosure };
      }
      if (event.type === "acknowledge-without-camera") {
        return {
          status: "coaching",
          acknowledged: state.disclosure,
          camera: false,
          cameraPermission: "undetermined",
        };
      }
      // Notably absent: `camera-permission-settled`. Nothing may report a
      // permission outcome from here, because nothing may have asked yet.
      return state;
    }

    case "requesting-camera": {
      if (event.type !== "camera-permission-settled") return state;
      // Every outcome lands in coaching. A refusal is not an error and must not
      // be a dead end — #2267 requires a working no-camera mode, and a consent
      // that costs her the feature is not freely given.
      return {
        status: "coaching",
        acknowledged: state.acknowledged,
        camera: event.permission === "granted",
        cameraPermission: event.permission,
      };
    }

    case "coaching": {
      if (event.type === "turn-camera-off") {
        return { ...state, camera: false };
      }
      if (event.type === "turn-camera-on") {
        // Only if the grant is still in hand. Re-prompting from here would mean
        // an OS dialog mid-injection, which is the worst possible moment.
        if (state.cameraPermission !== "granted") return state;
        return { ...state, camera: true };
      }
      return state;
    }
  }
}

/** Whether the live camera view may be rendered right now. */
/**
 * ### What the walk uses, and what it does not, on purpose
 *
 * The sheet drives this machine through the acknowledgement. Once the walk
 * starts it carries its own `cameraOn`, and the readers below have no caller —
 * a review flagged that as the disclosure's promise being honoured by the
 * interface while the object built to record it never hears about it.
 *
 * Left as it is, deliberately. The app posts nothing to the coach API today —
 * there is not one call to it in the codebase — so there is no receipt to be
 * wrong: the machine is a local object, and rebranching the walk onto it now
 * would be rearranging state nobody reads. It becomes real the day the app is
 * wired to the endpoints in dashboard-api #1834, when "she consented", "she
 * turned the camera off" and "she withdrew" have to leave the device as
 * events. That is the change that should adopt this machine, rather than doing
 * the work twice.
 */
export function mayUseCamera(state: CoachConsentState): boolean {
  return state.status === "coaching" && state.camera;
}

/** Whether coaching may proceed at all, with or without the camera. */
export function mayCoach(state: CoachConsentState): boolean {
  return state.status === "coaching";
}
